import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  ArrowLeft, Award, BookOpen, Clock, Download, Flame, GraduationCap,
  ShieldCheck, Target, Sparkles, FileText, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export default function Transcript() {
  useDocumentMeta({
    title: "Official Transcript — APM Fire Alarm Exam Prep",
    description: "Your complete academic transcript: hours by source, assessment record, credentials earned.",
    path: "/transcript",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const transcriptRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/transcript");
        setData(d);
      } catch {
        toast.error("Could not load transcript");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadPdf = async () => {
    if (!transcriptRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(transcriptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      const imgW = pageW - 40;
      const imgH = imgW / ratio;
      // Multi-page support if imgH > pageH
      if (imgH <= pageH - 40) {
        pdf.addImage(imgData, "PNG", 20, 20, imgW, imgH);
      } else {
        // Slice the canvas into page-height chunks
        const pageHeightPx = ((pageH - 40) * canvas.width) / imgW;
        let yOffset = 0;
        while (yOffset < canvas.height) {
          const sliceH = Math.min(pageHeightPx, canvas.height - yOffset);
          const slice = document.createElement("canvas");
          slice.width = canvas.width;
          slice.height = sliceH;
          slice.getContext("2d").drawImage(canvas, 0, yOffset, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
          const sliceData = slice.toDataURL("image/png");
          const sliceImgH = (imgW * sliceH) / canvas.width;
          if (yOffset > 0) pdf.addPage();
          pdf.addImage(sliceData, "PNG", 20, 20, imgW, sliceImgH);
          yOffset += sliceH;
        }
      }
      const safe = (data?.student?.name || "student").replace(/\s+/g, "_");
      pdf.save(`APM_Transcript_${safe}.pdf`);
      toast.success("Transcript PDF downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading transcript…</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">
          No transcript data available.
          <Link to="/dashboard" className="ml-2 text-red-600 underline">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const { student, hours, verified_study, main_curriculum, tracks, assessments, credentials, tutor, issued_at } = data;
  const issuedDate = new Date(issued_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="transcript-page">
        {/* Top action bar (excluded from PDF) */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-8">
          <Link to="/dashboard" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1" data-testid="transcript-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <button
            onClick={downloadPdf}
            disabled={downloading}
            className="btn-primary"
            data-testid="transcript-download-pdf"
          >
            <Download className="w-4 h-4" /> {downloading ? "Generating…" : "Download PDF"}
          </button>
        </div>

        {/* Printable transcript body */}
        <div ref={transcriptRef} className="bg-white" data-testid="transcript-body">
          {/* Header */}
          <header className="border-b-4 border-red-600 pb-6 mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-600 grid place-items-center">
                  <Flame className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-heading text-xl font-bold tracking-tight">APM Fire Alarm Exam Prep</div>
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">Est. 2026 · Official Transcript</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Issued</div>
                <div className="font-mono text-sm">{issuedDate}</div>
              </div>
            </div>
          </header>

          {/* Student identity block */}
          <section className="mb-10" data-testid="transcript-student">
            <div className="overline mb-2"><span className="red-bar" />Student</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">Name</div>
                <div className="font-heading text-2xl font-bold tracking-tight">{student.name}</div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">Email</div>
                <div className="font-mono text-sm">{student.email}</div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">Enrolled</div>
                <div className="font-mono text-sm">
                  {new Date(student.joined).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">Student ID</div>
                <div className="font-mono text-xs text-zinc-700 break-all">{student.id}</div>
              </div>
            </div>
          </section>

          {/* Hours summary — 4-stat grid */}
          <section className="mb-10" data-testid="transcript-hours">
            <div className="overline mb-2"><span className="red-bar" />Hours summary</div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="border-l-4 border-red-600 pl-4 py-2">
                <div className="font-heading text-3xl font-bold tracking-tight" data-testid="transcript-hours-combined">
                  {hours.combined_completed}
                  <span className="text-zinc-400 font-mono text-lg"> / {hours.combined_total}</span>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Combined hours</div>
              </div>
              <div className="border-l-2 border-zinc-300 pl-4 py-2">
                <div className="font-heading text-2xl font-bold tracking-tight">
                  {hours.main_completed}<span className="text-zinc-400 font-mono text-base"> / {hours.main_total}</span>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">NICET (L1·L2·L3·S-98)</div>
              </div>
              <div className="border-l-2 border-zinc-300 pl-4 py-2">
                <div className="font-heading text-2xl font-bold tracking-tight">
                  {hours.track_completed}<span className="text-zinc-400 font-mono text-base"> / {hours.track_total}</span>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Specialty tracks</div>
              </div>
              <div className="border-l-2 border-zinc-300 pl-4 py-2">
                <div className="font-heading text-2xl font-bold tracking-tight">{hours.combined_percent}%</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Overall progress</div>
              </div>
            </div>
          </section>

          {/* Verified study time (time-on-task) */}
          {verified_study && (verified_study.total_minutes > 0 || true) && (
            <section className="mb-10" data-testid="transcript-verified-study">
              <div className="overline mb-2"><span className="red-bar" />Verified study time</div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="card-tech p-4">
                  <div className="font-heading text-2xl font-bold tracking-tight" data-testid="transcript-verified-total">
                    {verified_study.total_minutes}
                    <span className="text-zinc-400 font-mono text-base"> min</span>
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Total time-on-task</div>
                </div>
                <div className="card-tech p-4">
                  <div className="font-heading text-2xl font-bold tracking-tight">{verified_study.main_minutes}<span className="text-zinc-400 font-mono text-base"> min</span></div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">NICET curriculum</div>
                </div>
                <div className="card-tech p-4">
                  <div className="font-heading text-2xl font-bold tracking-tight">{verified_study.track_minutes}<span className="text-zinc-400 font-mono text-base"> min</span></div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Specialty tracks</div>
                </div>
              </div>
              <p className="text-[11px] font-mono text-zinc-500 mt-3">
                Automatically tracked while a lesson tab is active. Capped at 2× the lesson's nominal duration to prevent abuse.
              </p>
            </section>
          )}

          {/* Credentials earned */}
          <section className="mb-10" data-testid="transcript-credentials">
            <div className="overline mb-3"><span className="red-bar" />Credentials earned</div>
            {credentials.length === 0 ? (
              <div className="text-sm text-zinc-500 italic">No credentials yet. Pass the NICET Final Practice Exam (70%+) to earn your APM credential.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {credentials.map((c, i) => (
                  <div key={c.id || `${c.kind}-${i}`} className="card-tech p-4 flex items-start gap-3" data-testid={`transcript-credential-${i}`}>
                    <div className="w-10 h-10 bg-red-600 text-white grid place-items-center flex-shrink-0">
                      <Award className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading text-sm font-bold tracking-tight leading-snug">{c.name}</div>
                      {c.id && (
                        <div className="font-mono text-[10px] text-zinc-500 mt-1 break-all">
                          ID: {c.id}
                        </div>
                      )}
                      {c.score_pct !== null && c.score_pct !== undefined && (
                        <div className="text-[11px] text-zinc-600 mt-0.5">Score: {c.score_pct}%</div>
                      )}
                      {c.issued_at && (
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          Issued {new Date(c.issued_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Main curriculum breakdown — 15 modules */}
          <section className="mb-10" data-testid="transcript-main">
            <div className="overline mb-3"><span className="red-bar" />NICET curriculum — module-by-module hours</div>
            <div className="border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Module</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Lessons</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Hours</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">%</th>
                  </tr>
                </thead>
                <tbody>
                  {main_curriculum.map((m, idx) => (
                    <tr key={m.id} className={idx % 2 ? "bg-zinc-50/50" : ""} data-testid={`transcript-main-row-${m.id}`}>
                      <td className="px-3 py-2 font-medium">{m.title}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{m.lessons_completed} / {m.lessons_total}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{m.hours_completed} / {m.hours_total}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        <span className={m.percent === 100 ? "text-green-700 font-semibold" : ""}>{m.percent}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-zinc-100 border-t-2 border-zinc-300">
                  <tr>
                    <td className="px-3 py-2 font-semibold">TOTAL</td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{hours.main_completed} / {hours.main_total} h</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">
                      {hours.main_total ? Math.round((hours.main_completed / hours.main_total) * 100) : 0}%
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Specialty tracks breakdown */}
          <section className="mb-10" data-testid="transcript-tracks">
            <div className="overline mb-3"><span className="red-bar" />Specialty tracks — per-track hours</div>
            <div className="border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Track</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Lessons</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Hours</th>
                    <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">Final exam</th>
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((t, idx) => (
                    <tr key={t.id} className={idx % 2 ? "bg-zinc-50/50" : ""} data-testid={`transcript-track-row-${t.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{t.short_name}</div>
                        <div className="text-[11px] text-zinc-500">{t.name.replace(t.short_name + " — ", "")}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{t.lessons_completed} / {t.lessons_total}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{t.hours_completed} / {t.hours_total}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {t.credential_earned ? (
                          <span className="text-green-700 font-semibold">PASSED · {t.final_exam_pct}%</span>
                        ) : t.final_exam_pct !== null ? (
                          <span className="text-red-600">{t.final_exam_pct}% — retake</span>
                        ) : (
                          <span className="text-zinc-400">Not attempted</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-zinc-100 border-t-2 border-zinc-300">
                  <tr>
                    <td className="px-3 py-2 font-semibold">TOTAL</td>
                    <td className="px-3 py-2"></td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{hours.track_completed} / {hours.track_total} h</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Assessment record */}
          <section className="mb-10" data-testid="transcript-assessments">
            <div className="overline mb-3"><span className="red-bar" />Assessment record</div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="card-tech p-4">
                <ListChecks className="w-4 h-4 text-red-600 mb-2" />
                <div className="font-heading text-2xl font-bold">{assessments.total_attempts}</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Quiz attempts</div>
              </div>
              <div className="card-tech p-4">
                <Target className="w-4 h-4 text-red-600 mb-2" />
                <div className="font-heading text-2xl font-bold">{assessments.passed_attempts}</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Passes (70%+)</div>
              </div>
              <div className="card-tech p-4">
                <BookOpen className="w-4 h-4 text-red-600 mb-2" />
                <div className="font-heading text-2xl font-bold">{assessments.avg_score_pct}%</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Average score</div>
              </div>
              <div className="card-tech p-4">
                <GraduationCap className="w-4 h-4 text-red-600 mb-2" />
                <div className="font-heading text-2xl font-bold">
                  {assessments.final_exam_best?.percentage ?? "—"}{assessments.final_exam_best ? "%" : ""}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-1">Final exam best</div>
              </div>
            </div>
          </section>

          {/* AI Tutor engagement */}
          <section className="mb-10" data-testid="transcript-tutor">
            <div className="overline mb-3"><span className="red-bar" />AI Tutor engagement</div>
            <div className="card-tech p-4 flex flex-wrap items-center gap-x-8 gap-y-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-red-600" />
                <span className="font-heading text-xl font-bold">{tutor.sessions}</span>
                <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">sessions</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-heading text-xl font-bold">{tutor.questions_asked}</span>
                <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">questions asked</span>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="border-t-2 border-zinc-200 pt-6 mt-10">
            <div className="grid sm:grid-cols-2 gap-4 text-[11px] font-mono text-zinc-500">
              <div>
                <div className="uppercase tracking-widest text-zinc-700 mb-1">Verification</div>
                Credentials issued by APM Fire Alarm Exam Prep. Verify any credential ID at the issuer's verification portal.
              </div>
              <div className="text-right">
                <div className="uppercase tracking-widest text-zinc-700 mb-1">Authority</div>
                Aligned to NICET Level 1 Foundation, NFPA 72 (2022), UL 864 / 268 / 217, NEC Art. 760, NYC Fire Code Ch. 9, FDNY 3 RCNY.
              </div>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100 text-[10px] font-mono text-zinc-400">
              <span>APM Fire Alarm Exam Prep · Est. 2026</span>
              <span>Transcript generated {issuedDate}</span>
            </div>
          </footer>
        </div>

        {/* Footer actions outside printable area */}
        <div className="mt-10 flex items-center justify-between flex-wrap gap-3 text-sm text-zinc-500">
          <Link to="/profile" className="hover:text-red-600 inline-flex items-center gap-1" data-testid="transcript-profile-link">
            <FileText className="w-4 h-4" /> View profile stats
          </Link>
          {credentials.length > 0 && credentials[0].verify_path && (
            <Link to={credentials[0].verify_path} className="hover:text-red-600 inline-flex items-center gap-1" data-testid="transcript-verify-link">
              <ShieldCheck className="w-4 h-4" /> Public verify page
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}
